import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CompanyAPI from '../../api/companies';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  metrics: {},
  conversations: {}, // Add conversations storage
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingConversations: false, // Add conversation loading flag
    isLoadingMore: false, // Add loading more flag
  },
  meta: {
    totalEntries: 0,
    currentPage: 1,
    perPage: 15,
    totalPages: 0,
  },
  conversationsMeta: {}, // Add conversations meta storage
};

export const getters = {
  getCompanies($state) {
    return $state.records;
  },
  getCompany: $state => id => {
    return $state.records.find(record => record.id === Number(id)) || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getMeta($state) {
    return $state.meta;
  },
  getCompanyMetrics: $state => id => {
    return $state.metrics[id] || {};
  },
  getCompanyConversations: $state => id => {
    return $state.conversations[id] || [];
  },
  getConversationsMeta: $state => id => {
    return $state.conversationsMeta[id] || { total: 0, currentPage: 1, perPage: 20 };
  },
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    const isLoadingMore = params.append && params.page > 1;
    
    if (isLoadingMore) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isLoadingMore: true });
    } else {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: true });
    }

    try {
      const response = await CompanyAPI.get(
        params.page,
        params.sortAttr,
        params.search,
        params.status,
        params.plan
      );

      console.log('Store - Full API Response:', response); // Debug the full response
      console.log('Store - Response Data:', response.data); // Debug response.data

      if (params.append && params.page > 1) {
        commit(types.default.APPEND_COMPANIES, response.data.payload);
      } else {
        commit(types.default.SET_COMPANIES, response.data.payload);
      }
      
      commit(types.default.SET_COMPANIES_META, response.data.meta);
      
      if (isLoadingMore) {
        commit(types.default.SET_COMPANIES_UI_FLAG, { isLoadingMore: false });
      } else {
        commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
      }

      // Return the response data - this was missing!
      return response.data;
    } catch (error) {
      if (isLoadingMore) {
        commit(types.default.SET_COMPANIES_UI_FLAG, { isLoadingMore: false });
      } else {
        commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
      }
      throwErrorMessage(error);
      throw error;
    }
  },

  show: async ({ commit }, { companyId }) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CompanyAPI.show(companyId);
      commit(types.default.EDIT_COMPANY, response.data);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingItem: false });
      throwErrorMessage(error);
    }
  },

  getConversations: async ({ commit }, { companyId, page = 1, perPage = 20 }) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingConversations: true });
    try {
      const response = await CompanyAPI.getConversations(companyId, {
        page,
        per_page: perPage,
      });
      
      const conversations = response.data.conversations || [];
      const meta = response.data.meta || {};
      
      if (page === 1) {
        commit(types.default.SET_COMPANY_CONVERSATIONS, { companyId, conversations });
      } else {
        commit(types.default.ADD_COMPANY_CONVERSATIONS, { companyId, conversations });
      }
      
      commit(types.default.SET_COMPANY_CONVERSATIONS_META, { companyId, meta });
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingConversations: false });
      
      return { conversations, meta };
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetchingConversations: false });
      throwErrorMessage(error);
      throw error;
    }
  },

  create: async ({ commit }, companyData) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: true });
    try {
      const response = await CompanyAPI.create(companyData);
      commit(types.default.ADD_COMPANY, response.data);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: false });
      throwErrorMessage(error);
      throw error;
    }
  },

  update: async ({ commit }, { companyId, ...companyData }) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isUpdating: true });
    try {
      const response = await CompanyAPI.update(companyId, companyData);
      commit(types.default.EDIT_COMPANY, response.data);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
      throw error;
    }
  },

  delete: async ({ commit }, companyId) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isDeleting: true });
    try {
      await CompanyAPI.delete(companyId);
      commit(types.default.DELETE_COMPANY, companyId);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isDeleting: false });
      throwErrorMessage(error);
      throw error;
    }
  },

  search: async ({ commit }, params = {}) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: true });
    try {
      const response = await CompanyAPI.search(
        params.search,
        params.page,
        params.sortAttr,
        params.status,
        params.plan
      );
      commit(types.default.SET_COMPANIES, response.data.payload);
      commit(types.default.SET_COMPANIES_META, response.data.meta);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
      throwErrorMessage(error);
    }
  },

  filter: async ({ commit }, filterParams) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: true });
    try {
      const response = await CompanyAPI.filter(filterParams);
      commit(types.default.SET_COMPANIES, response.data.payload);
      commit(types.default.SET_COMPANIES_META, response.data.meta);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isFetching: false });
      throwErrorMessage(error);
    }
  },

  getMetrics: async ({ commit }, companyId) => {
    try {
      const response = await CompanyAPI.getMetrics(companyId);
      commit(types.default.SET_COMPANY_METRICS, {
        companyId,
        metrics: response.data.metrics,
      });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  bulkImport: async ({ commit }, file) => {
    commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: true });
    try {
      const response = await CompanyAPI.bulkImport(file);
      commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_COMPANIES_UI_FLAG, { isCreating: false });
      throwErrorMessage(error);
      throw error;
    }
  },
};

export const mutations = {
  [types.default.SET_COMPANIES_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_COMPANIES]: MutationHelpers.set,

  [types.default.APPEND_COMPANIES]($state, companies) {
    // Remove duplicates by ID and append new companies
    const existingIds = new Set($state.records.map(company => company.id));
    const newCompanies = companies.filter(company => !existingIds.has(company.id));
    $state.records = [...$state.records, ...newCompanies];
  },

  [types.default.SET_COMPANIES_META]($state, data) {
    console.log('Setting companies meta:', data);
    
    // Handle both camelCase and snake_case from API
    const totalPages = data.total_pages || Math.ceil((data.total_entries || data.totalEntries) / (data.per_page || data.perPage)) || 0;
    
    $state.meta = {
      totalEntries: data.total_entries || data.totalEntries || 0,
      currentPage: data.current_page || data.currentPage || 1,
      perPage: data.per_page || data.perPage || 15,
      totalPages: totalPages,
      // Keep the original data as well for compatibility
      ...data,
    };
    
    console.log('Meta after setting:', $state.meta);
  },

  [types.default.ADD_COMPANY]: MutationHelpers.create,

  [types.default.EDIT_COMPANY]($state, company) {
    const index = $state.records.findIndex(record => record.id === company.id);
    if (index !== -1) {
      $state.records.splice(index, 1, { ...$state.records[index], ...company });
    } else {
      $state.records.push(company);
    }
  },
  
  [types.default.DELETE_COMPANY]: MutationHelpers.destroy,

  [types.default.SET_COMPANY_METRICS]($state, { companyId, metrics }) {
    $state.metrics = {
      ...$state.metrics,
      [companyId]: metrics,
    };
  },

  [types.default.SET_COMPANY_CONVERSATIONS]($state, { companyId, conversations }) {
    $state.conversations = {
      ...$state.conversations,
      [companyId]: conversations,
    };
  },

  [types.default.ADD_COMPANY_CONVERSATIONS]($state, { companyId, conversations }) {
    const existingConversations = $state.conversations[companyId] || [];
    $state.conversations = {
      ...$state.conversations,
      [companyId]: [...existingConversations, ...conversations],
    };
  },

  [types.default.SET_COMPANY_CONVERSATIONS_META]($state, { companyId, meta }) {
    $state.conversationsMeta = {
      ...$state.conversationsMeta,
      [companyId]: meta,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
